import { Component, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { firstErrorMessage } from '../../forms/formly-errors';
import { FieldShellComponent } from '../../forms/field-shell.component';

const BASE =
  'w-full bg-white border rounded-[16px] px-4 py-3 font-base text-[15px] text-black placeholder:text-black/35 transition-colors focus:outline-none focus:ring-2 resize-y';
const OK = 'border-black/10 focus:border-mty-primary focus:ring-mty-primary/15';
const ERR = 'border-mty-danger focus:border-mty-danger focus:ring-mty-danger/15';

@Component({
  selector: 'app-formly-mat-textarea',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TextFieldModule, TranslateModule, FormlyModule, FieldShellComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-field-shell
      [label]="props.label || ''"
      [for]="id"
      [required]="!!props.required"
      [hint]="props['hint'] || ''"
      [errorMessage]="isInvalid() ? errorMessage() : ''">
      <textarea
        cdkTextareaAutosize
        [cdkAutosizeMinRows]="props['rows'] || 4"
        [cdkAutosizeMaxRows]="props['maxRows'] || 12"
        [id]="id"
        [formControl]="formControl"
        [formlyAttributes]="field"
        [placeholder]="(props.placeholder || '') | translate"
        [required]="!!props.required"
        [maxlength]="props.maxLength || null"
        [class]="inputClass()"
        [attr.aria-invalid]="isInvalid() ? 'true' : null"
        [attr.data-field-id]="field.id"></textarea>
      @if (props['charCount'] && props.maxLength) {
        <span class="self-end font-base text-[12px] text-black/55 -mt-0.5">
          {{ (formControl.value?.length || 0) }} / {{ props.maxLength }}
        </span>
      }
    </app-field-shell>
  `,
})
export class MatTextareaFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.formControl.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  inputClass(): string {
    return `${BASE} ${this.isInvalid() ? ERR : OK}`;
  }

  errorMessage(): string {
    return firstErrorMessage(this.formControl);
  }
}
